import { Component, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { DataDisplayComponent, ButtonComponent, DataContainerComponent } from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import {
  OrganizationDetails,
  OrganizationComment,
  AdmissionState,
  CommentType
} from '../../../../../services/organizations/organization.model';
import {
  CommentsComponent,
  Comment,
  CommentSubmission
} from '../../../../../components/organisms/comments/comments.component';
import { ProtectedContentComponent } from '../../../../../components/organisms/protected-content/protected-content.component';
import { AdmissionDecisionComponent } from '../../../../../components/organisms/admission-decision/admission-decision.component';
import {
  PdfViewerDialogComponent,
  PdfViewerDialogData
} from '../../../../../components/organisms/pdf-viewer/pdf-viewer.component';
import { NavigationService } from '@services/navigation.service';

@Component({
  selector: 'app-request-admission-page',
  imports: [
    AsyncPipe,
    CommonModule,
    MatCardModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    CommentsComponent,
    ProtectedContentComponent,
    ButtonComponent,
    AdmissionDecisionComponent
  ],
  templateUrl: './request-admission-page.component.html',
  styleUrl: './request-admission-page.component.scss'
})
export class RequestAdmissionPageComponent {
  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);
  private dialog = inject(MatDialog);

  requiredRoles = this.navigationService.getAccessRoles('admin/requests');

  approvalForm = this.fb.group({
    reasoning: ['', Validators.required],
    comment: ['', Validators.required]
  });

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  private dataRefresh$ = new BehaviorSubject<void>(undefined);

  data$: Observable<OrganizationDetails> = combineLatest([this.id$, this.dataRefresh$]).pipe(
    switchMap(([id]) => this.organizationService.getDetailsById(id))
  );

  admissionState$ = this.data$.pipe(map((data) => data.admissionState));

  private commentsRefresh$ = new BehaviorSubject<void>(undefined);
  private readonly commentsPageSize = 5;
  private commentsPage = 0;
  private allLoadedComments: OrganizationComment[] = [];

  private commentsState$ = new BehaviorSubject<{ items: OrganizationComment[]; hasMore: boolean }>({
    items: [],
    hasMore: false
  });

  commentsForBox$: Observable<Comment[]> = this.commentsState$.pipe(
    map((state) =>
      state.items.map((c) => ({
        id: c.id,
        author: c.author,
        text: c.text,
        date: c.date,
        type: c.type || CommentType.COMMENT,
        isPublic: c.isPublic
      }))
    )
  );

  hasMoreComments$: Observable<boolean> = this.commentsState$.pipe(map((state) => state.hasMore));

  existingReasoning$ = combineLatest([this.id$, this.admissionState$, this.commentsRefresh$]).pipe(
    switchMap(([id, admissionState]) => {
      if (
        admissionState === AdmissionState.APPROVED ||
        admissionState === AdmissionState.REJECTED
      ) {
        return this.organizationService
          .getComments(id, { page: 0, size: 1000, sort: 'date,desc' })
          .pipe(
            map((result) => {
              const targetType =
                admissionState === AdmissionState.APPROVED
                  ? CommentType.APPROVAL
                  : CommentType.REJECTION;
              const comment = result.content.find((c) => c.type === targetType);
              return comment?.text || 'Keine Begründung angegeben';
            }),
            catchError(() => of('Keine Begründung angegeben'))
          );
      }
      return of('');
    })
  );

  private commentsLoader$ = combineLatest([this.id$, this.commentsRefresh$])
    .pipe(
      switchMap(([id]) => {
        this.commentsPage = 0;
        this.allLoadedComments = [];
        return this.organizationService
          .getComments(id, { page: 0, size: this.commentsPageSize, sort: 'date,desc' })
          .pipe(
            catchError(() =>
              of({ content: [] as OrganizationComment[], pagination: { last: true } } as any)
            )
          );
      }),
      tap((result) => {
        this.allLoadedComments = result.content;
        this.commentsState$.next({
          items: this.allLoadedComments,
          hasMore: !result.pagination.last
        });
      })
    )
    .subscribe();

  userDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('approval.summary.name.label'),
          value: [
            [
              dto.responsiblePerson.title || '',
              dto.responsiblePerson.firstName,
              dto.responsiblePerson.lastName
            ]
              .filter(Boolean)
              .join(' ')
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.contactData.phoneNumber.label'),
          value: [dto.responsiblePerson.phoneNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.contactData.email.label'),
          value: [dto.responsiblePerson.email]
        },
        {
          label: this.translocoService.translate('approval.summary.position.label'),
          value: [dto.responsiblePerson.position]
        }
      ];
    })
  );

  organisationDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approval.summary.foundingDate.label'),
          value: [
            dto.foundingDate
              ? new Date(dto.foundingDate).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              : ''
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.address.label'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.commercialRegisterNumber.label'),
          value: [dto.commercialRegisterNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.commercialRegisterCourt.label'),
          value: [dto.commercialRegisterCourt]
        },
        {
          label: this.translocoService.translate('approval.summary.website.label'),
          value: [dto.website]
        },
        {
          label: this.translocoService.translate('approval.summary.taxIdNumber.label'),
          value: [dto.taxIdNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.sector.label'),
          value: [dto.sector]
        },
        {
          label: this.translocoService.translate('approval.summary.description.label'),
          value: [dto.description]
        }
      ];
    })
  );

  backToList() {
    void this.router.navigate(['admin', 'requests']);
  }

  loadMoreComments() {
    this.commentsPage++;
    this.id$
      .pipe(
        take(1),
        switchMap((id) =>
          this.organizationService.getComments(id, {
            page: this.commentsPage,
            size: this.commentsPageSize,
            sort: 'date,desc'
          })
        ),
        tap((result) => {
          this.allLoadedComments = [...this.allLoadedComments, ...result.content];
          this.commentsState$.next({
            items: this.allLoadedComments,
            hasMore: !result.pagination.last
          });
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }

  reject() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.organizationService.reject(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error rejecting request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  approve() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.organizationService.approve(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error approving request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  addComment() {
    if (this.approvalForm.get('comment')?.invalid) {
      this.approvalForm.controls.comment.markAsTouched();
      return;
    }

    const text = this.approvalForm.value.comment!;
    this.id$
      .pipe(
        switchMap((id) => this.organizationService.addComment(id, { text: text.trim() })),
        tap(() => {
          this.commentsRefresh$.next();
          this.approvalForm.controls.comment.setValue('');
        })
      )
      .subscribe();
  }

  onCommentAdded(text: string) {
    this.id$
      .pipe(
        switchMap((id) => this.organizationService.addComment(id, { text: text })),
        tap(() => this.commentsRefresh$.next()),
        catchError((error) => {
          console.error('Error adding comment:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  onCommentWithVisibility(submission: CommentSubmission) {
    this.id$
      .pipe(
        switchMap((id) =>
          this.organizationService.addComment(id, {
            text: submission.text,
            isPublic: submission.isPublic
          })
        ),
        tap(() => this.commentsRefresh$.next()),
        catchError((error) => {
          console.error('Error adding comment:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  openDocument(): void {
    this.id$
      .pipe(
        take(1),
        switchMap((id) => this.organizationService.getRegistrationDocument(id))
      )
      .subscribe({
        next: (blob: Blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            this.dialog.open(PdfViewerDialogComponent, {
              data: { pdfData: arrayBuffer } as PdfViewerDialogData,
              width: '80vw',
              maxWidth: '95vw',
              height: '90vh'
            });
          };
          reader.readAsArrayBuffer(blob);
        },
        error: (err) => {
          console.error('Error loading document:', err);
          this.notificationService.showError('error.failed');
        }
      });
  }
}
